import React, { useCallback, useState } from 'react';
import { Modal, Text, Button, Stack, Group, Select, Textarea, Alert } from '@mantine/core';
import { IconX, IconAlertCircle } from '@tabler/icons-react';
import { RedditMentionDto } from '@/domain/entities/RedditMention';
import { useRejectMentionMutation } from '@/application/queries/customerMentionQueries';

/**
 * Predefined rejection reasons
 * Sprint 037 Session 9: Customer self-confirmation UI
 */
const REJECTION_REASONS = [
  { value: 'not_my_business', label: 'Not my business' },
  { value: 'wrong_location', label: 'Wrong location' },
  { value: 'spam_irrelevant', label: 'Spam/Irrelevant' },
  { value: 'other', label: 'Other' }
] as const;

export interface RejectMentionDialogProps {
  isOpen: boolean;
  mention: RedditMentionDto | null;
  businessId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * RejectMentionDialog - Modal for business owners to reject a mention
 *
 * Sprint 037 Session 9: Customer self-confirmation UI
 *
 * Features:
 * - Required dropdown with predefined rejection reasons
 * - Optional free text field for additional context
 * - Reject button disabled until reason is selected
 * - Creates audit record with rejection reason
 *
 * Rejection Reasons:
 * - "Not my business" - The mention is not about this business
 * - "Wrong location" - The mention refers to a different location
 * - "Spam/Irrelevant" - The mention is spam or not relevant
 * - "Other" - Other reason (should provide additional comments)
 */
export const RejectMentionDialog: React.FC<RejectMentionDialogProps> = ({
  isOpen,
  mention,
  businessId,
  onClose,
  onSuccess
}): React.ReactElement => {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [additionalComments, setAdditionalComments] = useState<string>('');

  const rejectMutation = useRejectMentionMutation();

  /**
   * Reset form state
   */
  const resetForm = useCallback((): void => {
    setSelectedReason(null);
    setAdditionalComments('');
  }, []);

  /**
   * Handle dialog close
   */
  const handleClose = useCallback((): void => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  /**
   * Handle reject confirmation
   */
  const handleReject = useCallback(async (): Promise<void> => {
    if (!mention || !selectedReason) {
      return;
    }

    // Get the display label for the selected reason
    const reasonLabel = REJECTION_REASONS.find(r => r.value === selectedReason)?.label ?? selectedReason;

    try {
      await rejectMutation.mutateAsync({
        businessId,
        mentionId: mention.id,
        reason: reasonLabel,
        additionalComments: additionalComments.trim() || undefined
      });

      // Close dialog and notify parent
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is handled by mutation hook (notifications)
      console.error('Reject mention failed:', error);
    }
  }, [mention, selectedReason, businessId, additionalComments, rejectMutation, handleClose, onSuccess]);

  /**
   * Handle reason selection
   */
  const handleReasonChange = useCallback((value: string | null): void => {
    setSelectedReason(value);
  }, []);

  /**
   * Handle additional comments change
   */
  const handleCommentsChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setAdditionalComments(event.currentTarget.value);
  }, []);

  if (!mention) {
    return <></>;
  }

  // Check if reject button should be disabled
  const isRejectDisabled = !selectedReason || rejectMutation.isPending;

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title="Reject Mention"
      size="md"
      centered
    >
      <Stack gap="md">
        {/* Info Alert */}
        <Alert icon={<IconAlertCircle />} color="blue" variant="light">
          Please tell us why this mention doesn&apos;t belong to your business. This helps us improve our matching.
        </Alert>

        {/* Mention Preview */}
        <Stack gap={4} p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: '4px' }}>
          <Text size="sm" fw={600}>Mention:</Text>
          <Text size="sm" lineClamp={3} style={{ whiteSpace: 'pre-wrap' }}>
            {mention.content || mention.postTitle}
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            r/{mention.subreddit}
          </Text>
        </Stack>

        {/* Rejection Reason (Required) */}
        <Select
          label="Reason for rejection"
          placeholder="Select a reason"
          data={REJECTION_REASONS}
          value={selectedReason}
          onChange={handleReasonChange}
          required
          withAsterisk
        />

        {/* Additional Comments (Optional) */}
        <Textarea
          label="Additional comments"
          description="Optional - provide more context if needed"
          placeholder="Any additional details about why this isn't your business..."
          value={additionalComments}
          onChange={handleCommentsChange}
          minRows={3}
          maxRows={5}
        />

        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            onClick={handleClose}
            disabled={rejectMutation.isPending}
          >
            Cancel
          </Button>

          <Button
            color="red"
            leftSection={<IconX size={16} />}
            onClick={handleReject}
            loading={rejectMutation.isPending}
            disabled={isRejectDisabled}
          >
            Reject Mention
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
